import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * Composant SEO réutilisable pour gérer les métadonnées de chaque page
 * 
 * @param {string} title - Titre de la page
 * @param {string} description - Description meta (max 160 caractères recommandé)
 * @param {string} path - Chemin de la page (ex: "/about", "/services/sparring")
 * @param {string} image - URL de l'image OG (optionnel, défaut: og-image.jpg)
 * @param {string} type - Type OG (optionnel, défaut: "website")
 * @param {object} structuredData - Données structurées JSON-LD (optionnel)
 * @param {string} canonical - URL canonique personnalisée (optionnel)
 * @param {boolean} noIndex - Si true, la page ne sera pas indexée (optionnel)
 */
const SEO = ({
  title,
  description,
  path = '',
  image = '/images/og-image.jpg',
  type = 'website',
  structuredData = null,
  canonical = null,
  noIndex = false,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';
  
  const baseUrl = 'https://www.fomow.fr';
  const fullUrl = `${baseUrl}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  const canonicalUrl = canonical || fullUrl;
  
  // Titre formaté avec le nom de l'entreprise
  const formattedTitle = title.includes('FOMOW') ? title : `${title} | FOMOW`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <title>{formattedTitle}</title>
      <meta name="title" content={formattedTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:site_name" content="FOMOW" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Alternate language versions */}
      <link rel="alternate" hrefLang="fr" href={`${baseUrl}${path}`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${path}`} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
